import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Spinner } from "react-bootstrap";
import Select from "react-select";
import { useUpdateChamp } from "../../../hooks/CHAMPS/useChampsActions";
import { useGetFieldTypes, useGetFieldApplyStatuses } from "../../../hooks/CHAMPS/useChampMetadata";
import { useGetChamps } from "../../../hooks/CHAMPS/useGetChamps";
import { customSelectStyles } from "../../../utils/reactSelectStyles";
import { toast } from "react-toastify";

const EditChamp = ({ show, setShow, champ }) => {
  const updateChampMutation = useUpdateChamp();
  const { data: fieldTypesData, isLoading: typesLoading } = useGetFieldTypes();
  const { data: statusesData, isLoading: statusesLoading } = useGetFieldApplyStatuses();
  const { data: champsData } = useGetChamps();

  const [formData, setFormData] = useState({
    label: "",
    position: "",
    largeur: "",
    parent: null,
    regle_visibilite: "",
    valeur_defaut: "",
    placeholder: "",
    est_requis: false,
    est_editable: true,
    tous_clients: true,
    statut_application: "",
    type_champ: "",
  });

  useEffect(() => {
    if (champ && show) {
      setFormData({
        label: champ.label || "",
        position: champ.position || "",
        largeur: champ.width || "",
        parent: champ.parentFieldId ?? null,
        regle_visibilite: champ.visibility_rule || "",
        valeur_defaut: champ.default_value || "",
        placeholder: champ.placeholder || "",
        est_requis: champ.is_required || false,
        est_editable: champ.is_editable || false,
        tous_clients: champ.all_clients || false,
        statut_application: champ.field_apply_status || "",
        type_champ: champ.field_type?.id || "",
      });
    }
  }, [champ, show]);

  const handleClose = () => {
    setShow(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.label || !formData.type_champ || !formData.statut_application) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    // Ne pas envoyer options et clients avec le PUT du champ
    // Ils seront gérés séparément par des endpoints dédiés
    const champData = {
      label: formData.label,
      nom_colonne: champ.column_name,
      position: formData.position,
      largeur: formData.largeur,
      parentFieldId: formData.parent || null,
      regle_visibilite: formData.regle_visibilite || null,
      valeur_defaut: formData.valeur_defaut || "",
      placeholder: formData.placeholder || "",
      est_requis: formData.est_requis,
      est_editable: formData.est_editable,
      tous_clients: formData.tous_clients,
      statut_application: formData.statut_application,
      type_champ: parseInt(formData.type_champ),
    };

    updateChampMutation.mutate({ id: champ.id, champData }, {
      onSuccess: () => {
        toast.success("Champ modifié avec succès");
        handleClose();
      },
      onError: (error) => {
        toast.error(error.message || "Erreur lors de la modification du champ");
      },
    });
  };

  const fieldTypes = fieldTypesData?.data || [];
  const statuses = statusesData?.data || [];
  const champs = champsData?.data || [];

  const selectedTypeName = fieldTypes.find(t => t.id === parseInt(formData.type_champ))?.name;
  const isCheckboxType = selectedTypeName === "Checkbox";

  // Exclude the current champ from parent options to avoid self-referencing
  const parentOptions = champs
    .filter(c => c.id !== champ?.id)
    .map(c => ({ value: c.id, label: c.label }));

  const isLoading = typesLoading || statusesLoading;

  if (!champ) return null;

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Modifier le champ : {champ.label}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" style={{ color: "#f6c23e" }} />
          </div>
        ) : (
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Label <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="label"
                    value={formData.label}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nom de colonne</Form.Label>
                  <Form.Control
                    type="text"
                    value={champ.column_name}
                    disabled
                  />
                  <Form.Text className="text-muted">
                    Le nom de colonne ne peut pas être modifié
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Type de champ <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    name="type_champ"
                    value={formData.type_champ}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Sélectionnez un type</option>
                    {fieldTypes.map(type => (
                      <option key={type.id} value={type.id}>
                        {type.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Statut d'application <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    name="statut_application"
                    value={formData.statut_application}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Sélectionnez un statut</option>
                    {statuses.map(status => (
                      <option key={status.value} value={status.value}>
                        {status.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Champ parent</Form.Label>
                  <Select
                    isClearable
                    value={parentOptions.find(o => o.value === formData.parent) ?? null}
                    onChange={(opt) => setFormData(prev => ({ ...prev, parent: opt ? opt.value : null }))}
                    options={parentOptions}
                    placeholder="Aucun parent (champ racine)"
                    noOptionsMessage={() => "Aucun champ disponible"}
                    styles={customSelectStyles}
                  />
                  <Form.Text className="text-muted">
                    Laisser vide pour un champ racine
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Position</Form.Label>
                  <Form.Control
                    type="text"
                    name="position"
                    value={formData.position}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Largeur</Form.Label>
                  <Form.Control
                    type="text"
                    name="largeur"
                    value={formData.largeur}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Valeur par défaut</Form.Label>
                  {isCheckboxType ? (
                    <Form.Select
                      name="valeur_defaut"
                      value={formData.valeur_defaut || "0"}
                      onChange={handleChange}
                    >
                      <option value="0">Non (0)</option>
                      <option value="1">Oui (1)</option>
                    </Form.Select>
                  ) : (
                    <Form.Control
                      type="text"
                      name="valeur_defaut"
                      value={formData.valeur_defaut}
                      onChange={handleChange}
                    />
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Placeholder</Form.Label>
                  <Form.Control
                    type="text"
                    name="placeholder"
                    value={formData.placeholder}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Règle de visibilité</Form.Label>
                  <Form.Control
                    type="text"
                    name="regle_visibilite"
                    value={formData.regle_visibilite}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="est_requis"
                    label="Requis"
                    checked={formData.est_requis}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="est_editable"
                    label="Éditable"
                    checked={formData.est_editable}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="tous_clients"
                    label="Disponible pour tous les clients"
                    checked={formData.tous_clients}
                    onChange={handleChange}
                  />
                  <Form.Text className="text-muted">
                    {!formData.tous_clients && "Gérez les clients spécifiques via le bouton 'Gérer les clients' sur la page de détail"}
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Annuler
        </Button>
        <Button
          variant="warning"
          onClick={handleSubmit}
          disabled={updateChampMutation.isPending || isLoading}
        >
          {updateChampMutation.isPending ? (
            <>
              <Spinner size="sm" animation="border" className="me-2" />
              Modification...
            </>
          ) : (
            "Enregistrer"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditChamp;
